import styles from "./style.module.scss";

import { useAppSelector } from "@/store/hooks";
import { HiddenableContainer } from "@components/layout/HiddenableContainer";
import { useEffect, useRef, useState } from "react";
import { Button, Col } from "reactstrap";
import LoadingScreen from "@components/content/LoadingScreen";
import { DocumentsEntryType } from "../DocumentStatusList";
import ClientStatusForm from "../ClientStatusForm";
import cx from "classnames";
import { ClientStatusRowData, InterestConfigType, normalizeClientStatusRow } from "@utils/DocumentStatus";
import moment from "moment";
import { formatMoneyValue } from "@utils/Currency";
import InvoiceRecibeOffCanvas from "@components/layout/Offcanvas/InvoiceRecibeOffCanvas";
import { CloseInvoiceRecibePubSub, DocumentStatusListPubSub, OpenAddCreditAmountPubSub, OpenInvoiceRecibePubSub } from "@utils/pubsubs/PubSub";
import { showAlert } from "@utils/Alert";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { RecibeDataType } from "@/api/RecibeApi";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { toNumberFormat } from "@utils/Utils";
import { CreditAmountTarget } from "@utils/CreditAmount";
import { ClientStatusFormData } from "@/formTypes/ClientStatusFormDataType";
import { ClientApi } from "@/api/ClientApi";

type PayInvoicesFormProps = {
  useToggle?: boolean,
  fieldTitle?: string,
  manualRecibe?: boolean
}

const PayInvoicesForm = (props: PayInvoicesFormProps) => {
  const { manualRecibe = false } = props
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [recibeTotal, setRecibeTotal] = useState<number>(0);
  const [recibeDate, setRecibeDate] = useState<string>();
  const [recibeCurrency, setRecibeCurrency] = useState<CurrencyDataType>();
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentsEntryType[]>([]);
  const [interestExemptedDocuments, setInterestExemptedDocuments] = useState<string[]>([]);
  const [statusFormData, setStatusFormData] = useState<ClientStatusFormData>();
  const [normalizedDocuments, setNormalizedDocuments] = useState<ClientStatusRowData[]>([]);
  const suscriptionData = useAppSelector(state => state.suscription?.suscription)
  const formRef = useRef<DynamicFormRef>(null);

  useEffect(() => {
    const today = moment();
    setRecibeDate(today.format("YYYY-MM-DD"))
  }, [])

  useEffect(() => {
    if (recibeDate) {
      const interestConfig: InterestConfigType = {
        interestOnDue: Number(suscriptionData?.interest_on_due || 0),
        isInterestOnDue: suscriptionData?.is_interest_on_due || false,
        interestExemptedDocuments
      }
      normalizeData(interestConfig, recibeDate)
    }
  }, [selectedDocuments, recibeDate])

  const normalizeData = async (interestConfig: InterestConfigType, recibeDate: string) => {
    const normalizedData: ClientStatusRowData[] = await Promise.all(selectedDocuments.map((r) => normalizeClientStatusRow(recibeDate, r, interestConfig)));
    const total = normalizedData.reduce((a, v) => {
      return a + v.saldo
    }, Number(0));
    setRecibeTotal(Number(toNumberFormat(total)))
    setNormalizedDocuments(normalizedData)
    if (normalizedData.length > 0) {
      setRecibeCurrency(normalizedData[0].currency);
    }
  }

  const onDocumentToogle = (documents: DocumentsEntryType[], interestExemptedDocuments: string[]) => {
    setSelectedDocuments(documents);
    setInterestExemptedDocuments(interestExemptedDocuments)
  }

  const triggerDocumentCancellation = () => {
    if (!recibeCurrency) {
      showAlert("danger", "No fue posible determinar la moneda para este recibo")
      return
    }
    if (selectedDocuments.length > 0 && recibeTotal > 0) {
      OpenInvoiceRecibePubSub.publish({
        openOffCanvas: true,
        recibeTotal,
        recibeDate: `${recibeDate}`,
        selectedDocuments,
        interestExemptedDocuments,
        recibeCurrency,
      })
    } else {
      showAlert("danger", "Debe de seleccionar al menos una factura para generar un recibo")
    }
  }

  const triggerCreditAmount = async () => {
    const clientId = statusFormData?.clientId;
    if (!clientId || clientId == "-2" || clientId == "0") {
      showAlert("danger", "Seleccione un cliente para agregar un monto a favor")
      return
    }

    setIsLoading(true)
    try {
      // Solo se reparte sobre facturas y liquidaciones: un saldo a favor no recibe abonos.
      const payableDocuments = normalizedDocuments.filter(d => d.kind == "invoice" || d.kind == "settlement");
      const documents: CreditAmountTarget[] = payableDocuments.map(d => ({
        id: d.id,
        numero: d.numero,
        saldo: d.saldo,
        kind: d.kind == "settlement" ? "settlement" : "invoice"
      }));

      const currency = payableDocuments.length > 0
        ? payableDocuments[0].currency
        : await CurrencyApi.getDefaultCurrency();

      if (!currency?.id) {
        showAlert("danger", "No fue posible determinar la moneda para el monto a favor")
        return
      }

      const client = await ClientApi.get(`${clientId}`);

      OpenAddCreditAmountPubSub.publish({
        openOffCanvas: true,
        clientId: `${clientId}`,
        clientNombre: `${client?.nombre || ""}`,
        documents,
        currency,
        date: `${recibeDate}`
      })
    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", err.message)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onRecibeCreated = (generatedRecibe: RecibeDataType) => {
    formRef.current?.triggerFormSubmit()
    CloseInvoiceRecibePubSub.publish({})
    DocumentStatusListPubSub.publish({ action: "Clear" })
    resetRecibeData()
  }

  const resetRecibeData = () => {
    setSelectedDocuments([]);
    setInterestExemptedDocuments([])
  }

  return (
    <>
      <ClientStatusForm
        ref={formRef}
        preventMultipleCurrency
        useToggle
        disableProjectedDate
        forceClientSelect
        hideAdditionalActions
        filters={["pendientes", "vencidas"]}
        onDocumentToogle={onDocumentToogle}
        onShowResult={(formData) => {
          setStatusFormData(formData)
          setShowResults(true)
        }}
      >
        <Col className={cx([styles.pullRight, styles.stickyElement, styles.selectedInvoiceTotal])} xs="12" sm="5" md="4">
          <h3 className="mb-0 text-center">Total: <b>{formatMoneyValue(recibeTotal, recibeCurrency?.currency_id || 1)}</b></h3>
        </Col>
      </ClientStatusForm>

      <HiddenableContainer show={showResults}>
        <Button size="lg" color="success" type="button" onClick={() => triggerDocumentCancellation()}>
          Generar Recibo
        </Button>
        <Button
          className="ms-2"
          size="lg"
          color="primary"
          type="button"
          data-testid="add-credit-amount-button"
          onClick={() => triggerCreditAmount()}>
          Agregar Monto a Favor
        </Button>
      </HiddenableContainer>

      <InvoiceRecibeOffCanvas
        manualRecibe={manualRecibe}
        suscriptionData={suscriptionData || {}}
        onRecibeCreated={onRecibeCreated} />

      <LoadingScreen isLoading={isLoading} />
    </>
  )
}

export default PayInvoicesForm;